import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { TID } from "../lib/testIds";
import { useAuth } from "../contexts/AuthContext";
import { Crosshair, ShieldCheck, ChevronDown, Sparkles, Menu, X } from "lucide-react";

// Public navigation per Phase 1 spec. Existing routes (/, /about, /pricing,
// /who-its-for, /recent, /run-analysis) remain valid; this nav surfaces the
// V2 information architecture without removing anything.

const LEARN_LINKS = [
  { to: "/how-it-works",  label: "How It Works",     testid: "nav-how-it-works" },
  { to: "/tour",          label: "Product Tour",     testid: "nav-tour" },
  { to: "/discovery",     label: "AI Discovery",     testid: "nav-discovery" },
  { to: "/demo-library",  label: "Demo Library",     testid: "nav-demo-library" },
  { to: "/use-cases",     label: "Use Cases",        testid: "nav-use-cases" },
  { to: "/learning",      label: "Learning Center",  testid: "nav-learning" },
  { to: "/sample",        label: "Sample Report",    testid: "nav-sample" },
  { to: "/faq",           label: "FAQ",              testid: "nav-faq" },
];

const TOP_LINKS = [
  { to: "/",                  label: "Home",              testid: TID.navHome },
  { to: "/proposal-services", label: "Proposal Services", testid: "nav-proposal-services" },
  { to: "/pricing",           label: "Pricing",           testid: "nav-pricing" },
];

export default function Navbar() {
  const loc = useLocation();
  const { user } = useAuth();
  const isInternal = user && ["CEO", "Sales", "Analyst"].includes(user.role);
  const [learnOpen, setLearnOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const learnRef = useRef(null);

  useEffect(() => {
    setLearnOpen(false);
    setMobileOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (learnRef.current && !learnRef.current.contains(e.target)) setLearnOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isActive = (p) => loc.pathname === p;
  const learnActive = LEARN_LINKS.some((l) => isActive(l.to));

  const linkCls = (active) =>
    `text-[11px] tracking-[0.18em] uppercase font-mono px-3 py-2 transition-colors ${
      active ? "text-sky-400 border-b border-sky-400" : "text-slate-400 hover:text-slate-100 border-b border-transparent"
    }`;

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--border-soft)] bg-[var(--bg-primary)]/85 backdrop-blur-md">
      <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 border border-sky-500/60 flex items-center justify-center bg-sky-500/5 group-hover:bg-sky-500/15 transition">
            <Crosshair className="w-4 h-4 text-sky-400" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-[15px] tracking-tight text-slate-100">
              GOVBID<span className="text-sky-400">IQ</span>
            </div>
            <div className="font-mono text-[8.5px] text-slate-500 tracking-[0.25em] uppercase">Contract Intelligence</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {TOP_LINKS.slice(0, 1).map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className={linkCls(isActive(l.to))}>{l.label}</Link>
          ))}

          {/* Learn dropdown */}
          <div ref={learnRef} className="relative">
            <button onClick={() => setLearnOpen((o) => !o)} data-testid="nav-learn-toggle" aria-haspopup="true" aria-expanded={learnOpen} className={`${linkCls(learnActive)} inline-flex items-center gap-1`}>
              Learn <ChevronDown className={`w-3 h-3 transition-transform ${learnOpen ? "rotate-180" : ""}`} />
            </button>
            {learnOpen && (
              <div className="absolute right-0 mt-1 min-w-[220px] border border-[var(--border-soft)] bg-[var(--bg-primary)] shadow-2xl" data-testid="nav-learn-menu">
                {LEARN_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={l.testid} className={`block px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-mono ${isActive(l.to) ? "text-sky-400 bg-sky-500/10" : "text-slate-300 hover:bg-sky-500/10 hover:text-sky-300"}`}>
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {TOP_LINKS.slice(1).map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className={linkCls(isActive(l.to))}>{l.label}</Link>
          ))}

          <Link to="/strategy-call" data-testid="nav-strategy-call" className="ml-3 inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2">
            <Sparkles className="w-3.5 h-3.5" /> Strategy Call
          </Link>
          <Link to="/discovery" data-testid="nav-start-ai-discovery" className="ml-2 inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-2 transition-colors">
            <span className="w-1.5 h-1.5 bg-slate-950 pulse-dot" /> Start AI Discovery
          </Link>
          {isInternal && (
            <Link to="/internal/dashboard" data-testid="nav-internal" className="ml-2 inline-flex items-center gap-2 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[11px] tracking-[0.18em] uppercase px-3 py-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Internal
            </Link>
          )}
        </nav>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen((o) => !o)} className="lg:hidden p-2 text-slate-300" aria-label="Toggle navigation" data-testid="nav-mobile-toggle">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[var(--border-soft)] bg-[var(--bg-primary)]" data-testid="nav-mobile-menu">
          <div className="max-w-[1400px] mx-auto px-6 py-3 grid grid-cols-2 gap-1.5">
            {[...TOP_LINKS, ...LEARN_LINKS].map((l) => (
              <Link key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} className={`text-[11px] tracking-[0.18em] uppercase font-mono px-3 py-2.5 border ${isActive(l.to) ? "border-sky-500 text-sky-400 bg-sky-500/5" : "border-slate-800 text-slate-300"}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/strategy-call" data-testid="nav-strategy-call-mobile" className="text-[11px] tracking-[0.18em] uppercase font-mono px-3 py-2.5 border border-sky-500/60 text-sky-300 col-span-2 text-center">Strategy Call</Link>
            <Link to="/discovery" data-testid="nav-start-ai-discovery-mobile" className="text-[11px] tracking-[0.18em] uppercase font-mono px-3 py-2.5 bg-sky-500 text-slate-950 font-bold col-span-2 text-center">Start AI Discovery</Link>
            {isInternal && (
              <Link to="/internal/dashboard" data-testid="nav-internal-mobile" className="text-[11px] tracking-[0.18em] uppercase font-mono px-3 py-2.5 border border-sky-500/60 text-sky-300 col-span-2 text-center">Internal</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
